import { memo, useContext } from 'react'
import type { ReactNode } from 'react'
import FormatCurrency from 'components/Common/FormatCurrency'
import Caption from 'LuxKit/Typography/Caption'
import BodyText from 'LuxKit/Typography/BodyText'
import TextLink from 'LuxKit/TextLink'
import type { CalendarPickerMonthView, OtherRoomsFromPrice } from 'selectors/offerPage/offerPageSelectors'
import MobileMultipleRoomsDurationLabel from 'components/OfferPage/MultipleRoomsBooking/Mobile/MobileMultipleRoomsDurationLabel'
import { FormattedMessage, useIntl } from 'react-intl'
import { CalendarBurnContext } from '../CalendarBurnContext'
import { useCalendarBurnDiscountedPrice } from '../useCalendarBurnDiscountedPrice'

interface Props {
  duration: number
  month: CalendarPickerMonthView
  hasOtherAvailability: boolean
  /** Cheapest price in other rooms for this month, shown when the filtered room is sold out */
  otherRoomsFromPrice?: OtherRoomsFromPrice
  /** Whether a "Filter by room" selection is currently applied */
  roomFilterActive?: boolean
  enableCalendarRecommendations: boolean
  selected?: boolean
  soldOutText?: string
  hideChildPrices?: boolean
  markCheapestPrice?: boolean
  userPoints?: number
  loyaltyProductType?: App.LoyaltyProductType
  isMultiRoom?: boolean
}

function OfferCalendarHotelMonthTile(props: Props) {
  const {
    month,
    duration,
    hasOtherAvailability,
    otherRoomsFromPrice,
    roomFilterActive,
    enableCalendarRecommendations,
    selected,
    soldOutText,
    hideChildPrices,
    markCheapestPrice,
    userPoints = 0,
    loyaltyProductType,
    isMultiRoom,
  } = props

  const intl = useIntl()
  const { enabled: displayPointsPrice } = useContext(CalendarBurnContext)
  const subTextColour = selected ? undefined : 'light-foreground-alt'
  const priceDefaultColour = markCheapestPrice && month.cheapest ? 'highlight-primary-dark' : undefined
  const priceColour = month.isLuxPlusPrice && !selected ? 'lux-plus' : priceDefaultColour

  const showOtherRoomsPrice = !!otherRoomsFromPrice && !hideChildPrices && !soldOutText

  const displayPrice = useCalendarBurnDiscountedPrice({
    total: month.hotelTotal,
    hotelPrice: month.hotelTotal,
    loyaltyProductType,
    userPoints,
    enabled: !!(displayPointsPrice && userPoints > 0),
  })

  const defaultSoldOutText = intl.formatMessage({
    id: 'a9_J1XfErNWngNwr62oI',
    defaultMessage: 'Sold out',
  })

  const viewSimilarOffersText = intl.formatMessage({
    id: 'os5Oyb_wcznG2qIjMcHY',
    defaultMessage: 'View similar offers',
  })

  const allRoomsSoldOutText = intl.formatMessage({
    id: 'xsHOJDUO1r2747LFoo0E',
    defaultMessage: 'All rooms sold out.',
  })

  const soldOutOtherRoomsNoPriceText = intl.formatMessage({
    id: '0tk2sBBchrOmz8eH1vvj',
    defaultMessage: 'Sold out.',
  })

  const viewRoomsText = intl.formatMessage({
    id: 'b0xnOuwndheerj7ia8ea',
    defaultMessage: 'View rooms',
  })
  const selectCheckInDateText = intl.formatMessage({
    id: 'nST_Qwkzm7f4uwYjDakt',
    defaultMessage: 'Select check-in date',
  })
  const viewAvailabilityText = intl.formatMessage({
    id: 'njgWBcnPd_1rCJ4POO0T',
    defaultMessage: 'View availability',
  })

  return (
    <div>
      {!month.hotelAvailable && !hasOtherAvailability && <>
        <Caption variant="medium" colour={subTextColour}>
          {soldOutText ?? (roomFilterActive && enableCalendarRecommendations ? allRoomsSoldOutText : defaultSoldOutText)}
        </Caption>
        {enableCalendarRecommendations && <TextLink size="small" weight="regular">
          {viewSimilarOffersText}
        </TextLink>}
      </>}
      {!month.hotelAvailable && hasOtherAvailability && <>
        {otherRoomsFromPrice && showOtherRoomsPrice && <Caption variant="medium" colour={subTextColour} data-testid="month-tile-other-rooms-price">
          <FormattedMessage
            id="JmOTHzmpwFVNx9ggCjOW"
            defaultMessage="<b>Sold out.</b> Other rooms available from {price}"
            
            values={{
              b: (chunks: ReactNode) => <strong>{chunks}</strong>,
              price: <div>
                <BodyText as="span" variant="medium" weight="bold" colour={otherRoomsFromPrice.isLuxPlusPrice && !selected ? 'lux-plus' : undefined}>
                  <FormatCurrency value={otherRoomsFromPrice.total} />
                </BodyText>
              </div>,
            }}
          />
        </Caption>}
        {!showOtherRoomsPrice && <Caption variant="medium" colour={subTextColour}>{soldOutText ?? soldOutOtherRoomsNoPriceText}</Caption>}
        <TextLink size="small" weight="regular">
          {viewRoomsText}
        </TextLink>
      </>}
      {month.hotelAvailable && <>
        {hideChildPrices && <Caption variant="medium" colour="light-foreground-alt" underline>{!selected ? selectCheckInDateText : viewAvailabilityText}</Caption>}
        {!hideChildPrices && <div>
          {!isMultiRoom && <Caption variant="medium" colour={subTextColour}>{intl.formatMessage({
            id: 'eFQuLYehOtF6MeMi_3kT',
            defaultMessage: '{nights, plural, one {# night} other {# nights}} from',
          }, { nights: duration })}
          </Caption>}
          {isMultiRoom && <Caption variant="medium" colour={subTextColour}><MobileMultipleRoomsDurationLabel shouldDisplayRoom shouldDisplayFlight={false} /></Caption>}
          <Caption variant="medium" colour={(month.isLuxPlusPrice && !selected) ? 'lux-plus' : subTextColour}>
            <BodyText as="span" variant="medium" weight="bold" colour={priceColour}>
              <FormatCurrency value={displayPrice} />
            </BodyText>
          </Caption>
          {displayPointsPrice && userPoints > 0 && displayPrice !== month.hotelTotal && (
            <div><Caption variant="medium" colour="light-foreground-alt"><s><FormatCurrency value={month.hotelTotal} /></s></Caption></div>
          )}
        </div>}
      </> }
    </div>
  )
}

export default memo(OfferCalendarHotelMonthTile)
